import type { DocsService } from '../../doc';
import type { TagService } from '../../tag';
import type { Attachment, ResearchMode } from '../types';

/**
 * Make a short human-readable title from the user's query.
 *
 * Researchers paste structured input as queries — BibTeX entries, raw
 * PMIDs, DOIs, JSON snippets — none of which read as titles when shown
 * verbatim. This helper recognises the common shapes and reduces them
 * to a clean label:
 *
 *   @article{P2020pmid31857720, title = {DKC1 enhances …}, …}
 *     → "DKC1 enhances …"
 *   PMID:38901234           → "PMID:38901234"
 *   10.1234/abc.123         → "DOI:10.1234/abc.123"
 *   {anything wrapped in braces}  → "Pasted reference"
 *
 * Falls through to a smart-truncated version of the original query
 * (break at word boundary, 60 chars max). Trailing punctuation dropped.
 */
export function titleFromQuery(query: string): string {
  const cleaned = query.replace(/\s+/g, ' ').trim().replace(/[?.!。]+$/, '');
  if (!cleaned) return 'New Research';

  // BibTeX-style entry — try to extract the inner title field; if the
  // title isn't quote-delimited the entry is too noisy to use, fall
  // back to a generic label.
  if (/^@\w+\s*\{/.test(cleaned)) {
    const titleMatch = cleaned.match(/title\s*=\s*[{"]([^}"]+)[}"]/i);
    if (titleMatch?.[1]) {
      const inner = titleMatch[1].replace(/\s+/g, ' ').trim();
      return inner.length > 60 ? inner.slice(0, 57).trimEnd() + '…' : inner;
    }
    return 'Pasted reference';
  }

  // Standalone PMID (digits only, with or without the PMID: prefix)
  const pmidMatch = cleaned.match(/^(?:PMID\s*:?\s*)?(\d{6,10})$/i);
  if (pmidMatch?.[1]) return `PMID:${pmidMatch[1]}`;

  // Standalone DOI
  const doiMatch = cleaned.match(/^(?:DOI\s*:?\s*)?(10\.\d{4,}\/\S+)$/i);
  if (doiMatch?.[1]) {
    const id = doiMatch[1];
    return `DOI:${id.length > 50 ? id.slice(0, 47) + '…' : id}`;
  }

  // arXiv ID
  const arxivMatch = cleaned.match(/^(?:arXiv\s*:?\s*)?(\d{4}\.\d{4,5}(v\d+)?)$/i);
  if (arxivMatch?.[1]) return `arXiv:${arxivMatch[1]}`;

  if (cleaned.length <= 60) return cleaned;

  // Smart truncate at the last word boundary in the first 60 chars so
  // the title doesn't slice in the middle of a word.
  const cut = cleaned.slice(0, 60);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 30 ? cut.slice(0, lastSpace) : cut;
  return base.trimEnd() + '…';
}

/**
 * Derive a thread-context title from the AI's response.
 *
 * Priority:
 *   1. `keyFindings[0]` — the LLM's distilled #1 finding, usually a
 *      clean claim that reads as a title.
 *   2. First sentence of `synthesis` — clipped at a sentence boundary.
 *
 * Returns `null` if neither source provides a usable string. The
 * caller decides whether replacing the existing title is appropriate
 * (don't downgrade, respect user rename, etc.).
 */
export function titleFromSynthesis(opts: {
  keyFindings?: string[];
  synthesis?: string;
}): string | null {
  const kf = opts.keyFindings?.find(s => s && s.trim().length > 0)?.trim();
  if (kf) {
    const cleaned = stripMarkdownInline(kf);
    if (cleaned.length >= 6) return shortenTitle(cleaned);
  }
  const synth = opts.synthesis?.trim();
  if (synth) {
    const firstSentence = stripMarkdownInline(
      synth.split(/(?<=[.!?])\s+/)[0] ?? synth
    );
    if (firstSentence.length >= 12) return shortenTitle(firstSentence);
  }
  return null;
}

/**
 * Strip the most common markdown inline noise so the candidate title
 * reads as plain prose: bold/italic markers, inline-code backticks,
 * link wrappers, citation tokens. Not a full markdown parser — just
 * enough cleanup for title-bar use.
 */
function stripMarkdownInline(s: string): string {
  return s
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\[(?:PMID|DOI|arXiv)\s*:[^\]]+\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function shortenTitle(s: string): string {
  const cleaned = s.replace(/[?.!。]+$/, '').trim();
  if (cleaned.length <= 60) return cleaned;
  const cut = cleaned.slice(0, 60);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 30 ? cut.slice(0, lastSpace) : cut;
  return base.trimEnd() + '…';
}

/**
 * Find an existing workspace tag by its label, creating it if absent.
 * Tags are workspace-scoped so two different workspaces can have their
 * own "Research" tag with different colours.
 */
function ensureTag(
  tagService: TagService,
  label: string,
  color: string
): { id: string; tag: (pageId: string) => void } {
  const existing = tagService.tagList.tags$.value.find(
    t => t.value$.value === label
  );
  if (existing) {
    return { id: existing.id, tag: (pageId: string) => existing.tag(pageId) };
  }
  const created = tagService.tagList.createTag(label, color);
  return { id: created.id, tag: (pageId: string) => created.tag(pageId) };
}

/** Map each research mode to a human-readable tag label. */
function modeTagLabel(mode: ResearchMode): string {
  switch (mode) {
    case 'explore':
      return 'Explore';
    case 'analyze':
      return 'Analyze';
    case 'deep-research':
      return 'Deep Research';
    case 'auto':
    default:
      return 'Research';
  }
}

/** Tag colours keyed by mode. Tweak as visual design evolves. */
const MODE_TAG_COLOR: Record<ResearchMode, string> = {
  auto: '#6B7280',
  explore: '#3B82F6',
  analyze: '#10B981',
  'deep-research': '#8B5CF6',
};

/**
 * Map the backend `DatasetKind` enum (Phase B preflight output) to a
 * user-facing tag label + colour. Lets researchers filter the sidebar
 * to "all dose-response analyses" with one click.
 *
 * Phase D step 3 — auto-applied to analyze docs after the orchestration
 * complete event lands and we know the kind. The full "Save snippet"
 * UX (with explicit Snippet tag, opt-in via a button) needs a custom
 * BlockSuite block; auto-tagging is the leverage move that ships
 * discoverability without it.
 */
const DATASET_KIND_TAG: Record<string, { label: string; color: string }> = {
  'dose-response': { label: 'Dose-response', color: '#F59E0B' },
  'dose-response-multi': { label: 'Dose-response', color: '#F59E0B' },
  'hts-plate': { label: 'HTS', color: '#EC4899' },
  'differential-expr': { label: 'DEG', color: '#06B6D4' },
  'expression-matrix': { label: 'Expression matrix', color: '#0891B2' },
  survival: { label: 'Survival', color: '#7C3AED' },
  'generic-numeric': { label: 'Numeric data', color: '#64748B' },
  'generic-categorical': { label: 'Categorical data', color: '#64748B' },
};

/**
 * Tag an analyze doc with its detected dataset kind. Idempotent: if
 * the tag already exists on the doc (e.g. on a follow-up turn) the
 * underlying TagList.tag() is a no-op.
 *
 * Called from `use-orchestration-doc-writer` once the complete event
 * arrives with `datasetProfile.kind`. Failures are logged and
 * swallowed — tagging is decoration, not load-bearing.
 */
export function applyAnalyzeKindToDoc(opts: {
  tagService: TagService;
  docId: string;
  kind: string | undefined;
}): void {
  const { tagService, docId, kind } = opts;
  if (!kind) return;
  const entry = DATASET_KIND_TAG[kind];
  if (!entry) return;
  try {
    ensureTag(tagService, entry.label, entry.color).tag(docId);
  } catch (err) {
    console.warn('[research-doc] failed to apply dataset-kind tag:', err);
  }
}

/**
 * Apply the `Snippet` tag to an analyze doc when the user asked to
 * "save as snippet". Surfaces the doc under `/all?tag=Snippet`.
 *
 * Idempotent. Bookmarkable URL is the closest thing to a snippet
 * library without building a dedicated sidebar entry.
 */
export function applySnippetTagToDoc(opts: {
  tagService: TagService;
  docId: string;
}): void {
  const { tagService, docId } = opts;
  try {
    ensureTag(tagService, 'Snippet', '#F472B6').tag(docId);
  } catch (err) {
    console.warn('[research-doc] failed to apply Snippet tag:', err);
  }
}

export interface CreateResearchDocOptions {
  docsService: DocsService;
  tagService: TagService;
  query: string;
  mode: ResearchMode;
  sessionId: string;
  /** Optional — pre-computed title override (e.g. when caller has a better one). */
  titleOverride?: string;
}

export interface CreateResearchDocResult {
  docId: string;
  title: string;
}

/**
 * Create a fresh "Research" doc for an Explore query and tag/annotate it
 * so it appears as a distinct entry in the sidebar + sessions history.
 *
 *   - primaryMode = 'edgeless' (research output is visual)
 *   - "Research" tag + mode-specific tag ("Explore" / "Deep Research" / …)
 *   - custom properties: researchMode, queryText, sessionId, createdAt
 *   - title derived from the query, capped at 60 chars
 */
export async function createResearchDoc(
  opts: CreateResearchDocOptions
): Promise<CreateResearchDocResult> {
  const { docsService, tagService, query, mode, sessionId } = opts;

  const title = opts.titleOverride ?? titleFromQuery(query);
  const docRecord = docsService.createDoc({ primaryMode: 'edgeless' });

  await docsService.changeDocTitle(docRecord.id, title);
  applyResearchTagsAndProperties({
    docsService,
    tagService,
    docId: docRecord.id,
    query,
    mode,
    sessionId,
  });

  return { docId: docRecord.id, title };
}

/** Input shape for `applyResearchMetadataToDoc`. */
export interface ApplyResearchMetadataOptions {
  docsService: DocsService;
  tagService: TagService;
  docId: string;
  query: string;
  mode: ResearchMode;
  sessionId: string;
  /**
   * When true, only set the title if the doc currently has no title (or
   * the default "Untitled"). Prevents us from overwriting a title the
   * user has manually typed. Default: true.
   */
  preserveExistingTitle?: boolean;
}

/**
 * Annotate an EXISTING doc with research metadata. Used when a query is
 * submitted into a doc that's already open — we want the doc's sidebar
 * entry to appear as a research artifact (title + tags + custom props)
 * without creating a new doc and navigating away.
 *
 * `preserveExistingTitle` (default true) guards against overwriting a
 * title the user has already typed by hand.
 */
export async function applyResearchMetadataToDoc(
  opts: ApplyResearchMetadataOptions
): Promise<void> {
  const {
    docsService,
    tagService,
    docId,
    query,
    mode,
    sessionId,
    preserveExistingTitle = true,
  } = opts;

  const docRecord = docsService.list.doc$(docId).value;
  const currentTitle = docRecord?.meta$.value?.title?.trim() ?? '';
  const hasMeaningfulTitle =
    currentTitle.length > 0 && currentTitle !== 'Untitled';

  if (!(preserveExistingTitle && hasMeaningfulTitle)) {
    const newTitle = titleFromQuery(query);
    try {
      await docsService.changeDocTitle(docId, newTitle);
    } catch (err) {
      console.warn('[research-doc] failed to update title:', err);
    }
  }

  applyResearchTagsAndProperties({
    docsService,
    tagService,
    docId,
    query,
    mode,
    sessionId,
  });
}

/** Shared helper: attach the two baseline tags + custom properties. */
function applyResearchTagsAndProperties(opts: {
  docsService: DocsService;
  tagService: TagService;
  docId: string;
  query: string;
  mode: ResearchMode;
  sessionId: string;
}): void {
  const { docsService, tagService, docId, query, mode, sessionId } = opts;

  ensureTag(tagService, 'Research', '#3B82F6').tag(docId);
  const modeTag = ensureTag(
    tagService,
    modeTagLabel(mode),
    MODE_TAG_COLOR[mode] ?? '#3B82F6'
  );
  modeTag.tag(docId);

  const docRecord = docsService.list.doc$(docId).value;
  if (docRecord) {
    docRecord.setCustomProperty('researchMode', mode);
    docRecord.setCustomProperty('queryText', query);
    docRecord.setCustomProperty('sessionId', sessionId);
    docRecord.setCustomProperty('createdAt', new Date().toISOString());
  }
}

/**
 * Read the sessionId stored on a doc's custom properties. Returns null when
 * the doc has never been through `applyResearchMetadataToDoc` (e.g. a
 * pre-existing non-research doc, or the very first query in a fresh doc).
 *
 * Used by `centered-chat.handleFinish` to reuse the sessionId across turns
 * in the same doc — without this, every query generates a fresh UUID and
 * the backend treats each question as an orphan session.
 */
export function getSessionIdFromDoc(
  docsService: DocsService,
  docId: string
): string | null {
  const docRecord = docsService.list.doc$(docId).value;
  if (!docRecord) return null;
  const stored = docRecord.properties$.value['custom:sessionId'];
  return typeof stored === 'string' && stored.length > 0 ? stored : null;
}

/**
 * Read any `Attachment[]` persisted to this doc by prior turns. Returns
 * `[]` if the doc has no session attachments yet, or if the stored value
 * is unparseable (corruption guard — never throw, the chat submission
 * must not fail because of bad doc state).
 *
 * Persists across navigation: the user can attach a PDF on turn 1, close
 * the doc, reopen it a day later, and turn 2 still sees the attachment.
 */
export function getSessionAttachmentsFromDoc(
  docsService: DocsService,
  docId: string
): Attachment[] {
  const docRecord = docsService.list.doc$(docId).value;
  if (!docRecord) return [];
  const stored = docRecord.properties$.value['custom:sessionAttachments'];
  if (typeof stored !== 'string' || stored.length === 0) return [];
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (a): a is Attachment =>
        a &&
        typeof a === 'object' &&
        typeof a.id === 'string' &&
        typeof a.name === 'string' &&
        typeof a.type === 'string'
    );
  } catch {
    return [];
  }
}

/**
 * Persist the accumulated session attachments onto the doc. Stored as a
 * JSON string under `custom:sessionAttachments`. Capped at 10 entries
 * (matches the bundle-upload limit in CLAUDE.md) — drop oldest on overflow.
 */
export function setSessionAttachmentsOnDoc(
  docsService: DocsService,
  docId: string,
  attachments: Attachment[]
): void {
  const docRecord = docsService.list.doc$(docId).value;
  if (!docRecord) return;
  const capped = attachments.slice(-10);
  docRecord.setCustomProperty(
    'sessionAttachments',
    JSON.stringify(capped)
  );
}
